import { XIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type SerialisedResult = {
    id: string
    title: string
    category: string
    description: string
}

type Props = {
    email: string | null
    results: SerialisedResult[]
    resultsSummary: string
}

type SendStatus = "idle" | "sending" | "sent" | "error"

const SEND_RESULTS_FORM_ID = "5"
const GET_IN_TOUCH_FORM_ID = "6"
const SUBMIT_TIMEOUT_MS = 8000

function setFieldValue(form: Element, name: string, value: string) {
    const el = form.querySelector(
        `input[name="${name}"], textarea[name="${name}"]`,
    ) as HTMLInputElement | HTMLTextAreaElement | null
    if (el) {
        el.value = value
        // Notify any frameworks listening to input changes (jQuery, etc.)
        el.dispatchEvent(new Event("input", { bubbles: true }))
        el.dispatchEvent(new Event("change", { bubbles: true }))
    }
}

function findForm(formId: string): HTMLFormElement | null {
    return document.querySelector(
        `form[data-form_id="${formId}"]`,
    ) as HTMLFormElement | null
}

function useFluentFormSubmitter(formId: string) {
    const [status, setStatus] = useState<SendStatus>("idle")
    const timeoutRef = useRef<number | null>(null)

    useEffect(() => {
        const onSuccess = (event: Event) => {
            const detail = (event as CustomEvent<{ form?: { id?: string } }>)
                .detail
            const submittedId =
                detail?.form?.id || (event.target as HTMLFormElement)?.id
            // Fluent Forms gives the form id as `fluentform_<n>`; match either.
            if (
                submittedId === `fluentform_${formId}` ||
                submittedId === formId
            ) {
                if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
                setStatus("sent")
            }
        }
        document.addEventListener("fluentform_submission_success", onSuccess)
        return () => {
            document.removeEventListener(
                "fluentform_submission_success",
                onSuccess,
            )
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
        }
    }, [formId])

    const submit = (fields: Record<string, string>) => {
        const form = findForm(formId)
        if (!form) {
            // No host page (e.g. local preview without WordPress) — fake success after a beat
            setStatus("sending")
            window.setTimeout(() => setStatus("sent"), 600)
            return
        }
        Object.entries(fields).forEach(([k, v]) => setFieldValue(form, k, v))
        setStatus("sending")
        // Fluent Forms hooks into the form's submit event; use requestSubmit so
        // the browser fires submit listeners (form.submit() would skip them).
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit()
        } else {
            form.submit()
        }
        // Safety net: if the success event never fires, assume submitted after a delay
        timeoutRef.current = window.setTimeout(() => {
            setStatus((s) => (s === "sending" ? "sent" : s))
        }, SUBMIT_TIMEOUT_MS)
    }

    const reset = () => setStatus("idle")

    return { status, submit, reset }
}

export default function ResultsCTA({ email, results, resultsSummary }: Props) {
    const { t } = useTranslation()
    const [modalOpen, setModalOpen] = useState(false)
    const sendResults = useFluentFormSubmitter(SEND_RESULTS_FORM_ID)

    const handleSendResults = () => {
        if (sendResults.status !== "idle") return
        sendResults.submit({
            email: email || "",
            answers_json: JSON.stringify(results),
            results_summary: resultsSummary,
        })
    }

    return (
        <>
            <div className="bg-secondary/30 mt-6 flex flex-col items-center gap-5 rounded-2xl border border-[var(--color-secondary,#ffe0ad)] p-6 sm:mt-10 sm:p-8">
                <div className="flex flex-col items-center gap-1 text-center">
                    <h3 className="text-text-primary text-xl font-bold sm:text-2xl">
                        {t("wizard.public_risk_wizard.results_cta.heading")}
                    </h3>
                    <p className="text-text-secondary max-w-xl text-sm sm:text-base">
                        {t("wizard.public_risk_wizard.results_cta.subheading")}
                    </p>
                </div>

                <div className="flex w-full max-w-md flex-col gap-3">
                    <PrimaryButton
                        status={sendResults.status}
                        email={email}
                        onClick={handleSendResults}
                    />
                    <SecondaryButton onClick={() => setModalOpen(true)} />
                </div>
            </div>

            {modalOpen && (
                <GetInTouchModal
                    email={email}
                    resultsSummary={resultsSummary}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    )
}

function PrimaryButton({
    status,
    email,
    onClick,
}: {
    status: SendStatus
    email: string | null
    onClick: () => void
}) {
    const { t } = useTranslation()
    const disabled = status === "sending" || status === "sent"

    const label =
        status === "sent"
            ? t("wizard.public_risk_wizard.results_cta.email_sent", {
                  email: email || "",
              })
            : status === "sending"
              ? t("wizard.public_risk_wizard.results_cta.email_sending")
              : t("wizard.public_risk_wizard.results_cta.email_button")

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`flex w-full items-center justify-center gap-2 rounded-[15px] px-5 py-3 text-base font-semibold transition-colors ${
                status === "sent"
                    ? "cursor-default bg-green-600 text-white"
                    : status === "sending"
                      ? "cursor-wait bg-[var(--color-primary,#ff9d00)]/80 text-white"
                      : "cursor-pointer bg-[var(--color-primary,#ff9d00)] text-white hover:bg-[var(--color-primary-hover,#ffb947)] active:bg-[var(--color-primary-active,#e68a00)]"
            }`}
        >
            <span aria-hidden="true">
                {status === "sent" ? "✓" : status === "sending" ? "⋯" : "✉"}
            </span>
            <span>{label}</span>
        </button>
    )
}

function SecondaryButton({ onClick }: { onClick: () => void }) {
    const { t } = useTranslation()
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[15px] border-2 border-[var(--color-primary,#ff9d00)] bg-transparent px-5 py-3 text-base font-semibold text-[var(--color-text-on-secondary,#000000)] transition-colors hover:bg-[var(--color-primary-hover,#ffb947)] hover:text-black"
        >
            <span>{t("wizard.public_risk_wizard.results_cta.contact_button")}</span>
            <span aria-hidden="true">→</span>
        </button>
    )
}

function GetInTouchModal({
    email,
    resultsSummary,
    onClose,
}: {
    email: string | null
    resultsSummary: string
    onClose: () => void
}) {
    const { t } = useTranslation()
    const getInTouch = useFluentFormSubmitter(GET_IN_TOUCH_FORM_ID)
    const [contactEmail, setContactEmail] = useState(email || "")
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [company, setCompany] = useState("")
    const [message, setMessage] = useState("")
    const dialogRef = useRef<HTMLDivElement | null>(null)

    // Close on Escape, prevent body scroll while open
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }
        document.addEventListener("keydown", onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.removeEventListener("keydown", onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [onClose])

    // Focus the first name input when opened
    useEffect(() => {
        const node = dialogRef.current?.querySelector(
            'input[name="first_name"]',
        ) as HTMLInputElement | null
        node?.focus()
    }, [])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (getInTouch.status !== "idle") return
        getInTouch.submit({
            email: contactEmail,
            first_name: firstName,
            last_name: lastName,
            company,
            message,
            assessment_summary: resultsSummary,
        })
    }

    const isSent = getInTouch.status === "sent"
    const isSending = getInTouch.status === "sending"

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={dialogRef}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 cursor-pointer rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label={t("common.general.close", { defaultValue: "Close" })}
                >
                    <XIcon size={20} weight="bold" />
                </button>

                {!isSent ? (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6 sm:p-8">
                        <div className="flex flex-col gap-1">
                            <h3 className="text-text-primary text-xl font-bold sm:text-2xl">
                                {t("wizard.public_risk_wizard.contact_modal.heading")}
                            </h3>
                            <p className="text-text-secondary text-sm">
                                {t("wizard.public_risk_wizard.contact_modal.subheading")}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <ModalInput
                                name="first_name"
                                label={t("wizard.public_risk_wizard.contact_modal.first_name")}
                                value={firstName}
                                onChange={setFirstName}
                                required
                            />
                            <ModalInput
                                name="last_name"
                                label={t("wizard.public_risk_wizard.contact_modal.last_name")}
                                value={lastName}
                                onChange={setLastName}
                                required
                            />
                        </div>

                        <ModalInput
                            name="email"
                            label={t("wizard.public_risk_wizard.contact_modal.email")}
                            value={contactEmail}
                            onChange={setContactEmail}
                            type="email"
                            required
                        />

                        <ModalInput
                            name="company"
                            label={t("wizard.public_risk_wizard.contact_modal.company")}
                            value={company}
                            onChange={setCompany}
                        />

                        <div className="flex flex-col gap-1">
                            <label
                                htmlFor="rw-contact-message"
                                className="text-text-primary text-sm font-medium"
                            >
                                {t("wizard.public_risk_wizard.contact_modal.message")}
                            </label>
                            <textarea
                                id="rw-contact-message"
                                rows={3}
                                placeholder={t(
                                    "wizard.public_risk_wizard.contact_modal.message_placeholder",
                                )}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--color-primary,#ff9d00)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#ff9d00)]/30"
                            />
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {t("wizard.public_risk_wizard.contact_modal.cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={isSending}
                                className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors ${
                                    isSending
                                        ? "cursor-wait bg-[var(--color-primary,#ff9d00)]/80"
                                        : "cursor-pointer bg-[var(--color-primary,#ff9d00)] hover:bg-[var(--color-primary-hover,#ffb947)] active:bg-[var(--color-primary-active,#e68a00)]"
                                }`}
                            >
                                {isSending
                                    ? t("wizard.public_risk_wizard.contact_modal.sending")
                                    : t("wizard.public_risk_wizard.contact_modal.submit")}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="flex flex-col items-center gap-3 p-8 text-center sm:p-10">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-3xl text-green-600">
                            ✓
                        </div>
                        <h3 className="text-text-primary text-xl font-bold">
                            {t("wizard.public_risk_wizard.contact_modal.success_heading")}
                        </h3>
                        <p className="text-text-secondary text-sm">
                            {t("wizard.public_risk_wizard.contact_modal.success_body")}
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-2 rounded-lg bg-[var(--color-primary,#ff9d00)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover,#ffb947)]"
                        >
                            {t("wizard.public_risk_wizard.contact_modal.success_close")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

function ModalInput({
    name,
    label,
    value,
    onChange,
    type = "text",
    required = false,
    disabled = false,
}: {
    name: string
    label: string
    value: string
    onChange: (value: string) => void
    type?: string
    required?: boolean
    disabled?: boolean
}) {
    const id = `rw-contact-${name}`
    return (
        <div className="flex flex-col gap-1">
            <label htmlFor={id} className="text-text-primary text-sm font-medium">
                {label}
                {required && <span className="text-red-500"> *</span>}
            </label>
            <input
                id={id}
                name={name}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                disabled={disabled}
                className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--color-primary,#ff9d00)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#ff9d00)]/30 ${disabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""}`}
            />
        </div>
    )
}
